/**
 * Find-a-Pro provider directory.
 *
 * We don't run our own pro database — we link out to established
 * directories with the user's ZIP and chosen trade pre-filled. URL
 * templates use `{trade}` and `{zip}` placeholders.
 *
 * Policy on what we include here:
 *   • Only providers with a stable deep-link URL that actually lands
 *     on relevant results. If the directory's deep link is fragile
 *     (Angi, HomeAdvisor — both fall back to Google "site:" search,
 *     which lands on a Google SERP and confuses users), it doesn't
 *     belong here. We previously had those and removed them.
 *   • Chain entries point at the chain's home page because that's
 *     where the user enters their ZIP into the chain's own locator;
 *     it's honest and reliable.
 *   • Emergency-flagged providers float to the top of the chain
 *     list and surface in an "emergency" category for plumbing /
 *     restoration emergencies.
 *
 * `tier: "featured"` — top-of-page Google-curated picks.
 * `tier: "search"` — general directories the user picks based on style.
 * `tier: "chain"` — national-chain services.
 */

export type ProviderTier = 'featured' | 'search' | 'chain';

export interface ProProvider {
  key: string;
  name: string;
  tagline: string;
  url: string;
  tier: ProviderTier;
  /** True when the deep link does not include the trade query — i.e.
   *  it's a home-page link the user has to interact with themselves.
   *  Drives the card's secondary copy. */
  isLandingOnly?: boolean;
  /** Emergency-grade — used for 24/7 plumbing, restoration, etc.
   *  Surfaces these in the Emergency category and on chain cards. */
  isEmergency?: boolean;
}

export const PRO_TRADES = [
  { key: 'plumber', label: 'Plumber' },
  { key: 'electrician', label: 'Electrician' },
  { key: 'hvac technician', label: 'HVAC technician' },
  { key: 'roofer', label: 'Roofer' },
  { key: 'landscaper', label: 'Landscaper' },
  { key: 'arborist tree service', label: 'Arborist / tree service' },
  { key: 'pest control', label: 'Pest control' },
  { key: 'septic service', label: 'Septic service' },
  { key: 'chimney sweep', label: 'Chimney sweep' },
  { key: 'painter', label: 'Painter' },
  { key: 'handyman', label: 'Handyman' },
  { key: 'house cleaner', label: 'House cleaner' },
  { key: 'pool service', label: 'Pool service' },
  { key: 'snow removal', label: 'Snow removal' },
  { key: 'generator service', label: 'Generator service' },
  { key: 'home inspector', label: 'Home inspector' },
  { key: 'window cleaner', label: 'Window cleaner' },
  { key: 'gutter cleaning', label: 'Gutter cleaning' },
  { key: 'masonry chimney repair', label: 'Masonry / chimney repair' },
  { key: 'foundation specialist', label: 'Foundation specialist' },
  { key: 'water damage restoration', label: 'Water damage restoration' },
  { key: 'mold remediation', label: 'Mold remediation' },
  { key: 'fence installer', label: 'Fence installer' },
  { key: 'deck builder', label: 'Deck builder' },
  { key: 'solar installer', label: 'Solar installer' },
  { key: 'EV charger installer', label: 'EV charger installer' },
  { key: 'lawn care', label: 'Lawn care' },
  { key: 'pressure washing', label: 'Pressure washing' },
  { key: 'garage door repair', label: 'Garage door repair' },
  { key: 'appliance repair', label: 'Appliance repair' },
  { key: 'water heater installer', label: 'Water heater installer' },
  { key: 'locksmith', label: 'Locksmith' },
] as const;

/**
 * Top-tier search surfaces with rock-solid deep links.
 *
 * Wave 49: Google Local Services REMOVED — its prolist deep link only
 * returns results for trades/areas with active Local Services Ads
 * coverage; everywhere else it renders an empty "no providers" page,
 * which reads as the app being broken. Google Maps covers the same
 * ground reliably (and shows the Guaranteed badge inline when it
 * exists).
 */
const PROVIDERS_FEATURED: ProProvider[] = [
  {
    key: 'google_maps',
    name: 'Google Maps',
    tagline: 'Local listings with reviews + directions',
    url: 'https://www.google.com/maps/search/{trade}+near+{zip}',
    tier: 'featured',
  },
  {
    key: 'yelp',
    name: 'Yelp',
    tagline: 'Reviews-first local directory',
    url: 'https://www.yelp.com/search?find_desc={trade}&find_loc={zip}',
    tier: 'featured',
  },
];

/**
 * Conservative search-directory list — only providers whose public
 * deep-link URLs we've verified actually land on relevant results
 * with the {trade} + {zip} substitution baked in. Thumbtack, Houzz,
 * TaskRabbit, and Porch were tried and dropped because their
 * structured-URL patterns are either unstable, behind login walls, or
 * require predefined slugs we can't map our free-text trades to.
 *
 * If we add anything here in the future, the bar is: paste a real
 * URL with a plumber/48473 substitution into a browser, hit return,
 * and confirm the page renders the right results without a redirect.
 */
const PROVIDERS_SEARCH: ProProvider[] = [
  {
    key: 'bbb',
    name: 'Better Business Bureau',
    tagline: 'Accreditation + complaint records',
    url: 'https://www.bbb.org/search?find_country=USA&find_loc={zip}&find_text={trade}',
    tier: 'search',
  },
  {
    key: 'nextdoor',
    name: 'Nextdoor',
    tagline: 'Neighbor recommendations',
    // Nextdoor doesn't support ZIP-scoped public deep links — when the
    // user lands there, their account's address scopes the search.
    url: 'https://nextdoor.com/search/?query={trade}',
    tier: 'search',
  },
];

const PROVIDERS_CHAIN: ProProvider[] = [
  {
    key: 'roto_rooter',
    name: 'Roto-Rooter',
    tagline: '24/7 plumbing + drain service',
    url: 'https://www.rotorooter.com/',
    tier: 'chain',
    isLandingOnly: true,
    isEmergency: true,
  },
  {
    key: 'mr_rooter',
    name: 'Mr. Rooter Plumbing',
    tagline: 'National plumbing franchise',
    url: 'https://www.mrrooter.com/',
    tier: 'chain',
    isLandingOnly: true,
    isEmergency: true,
  },
  {
    key: 'servicemaster',
    name: 'ServiceMaster Restore',
    tagline: 'Water / fire damage restoration',
    url: 'https://www.servicemasterrestore.com/',
    tier: 'chain',
    isLandingOnly: true,
    isEmergency: true,
  },
  {
    key: 'servpro',
    name: 'SERVPRO',
    tagline: '24/7 fire + water damage cleanup',
    url: 'https://www.servpro.com/',
    tier: 'chain',
    isLandingOnly: true,
    isEmergency: true,
  },
  {
    key: 'home_depot',
    name: 'Home Depot Home Services',
    tagline: 'Vetted installs — use their on-site locator',
    // Their /services hub redirects to a path that 404s for some
    // visitors; home page is the durable entry point.
    url: 'https://www.homedepot.com/',
    tier: 'chain',
    isLandingOnly: true,
  },
  {
    key: 'lowes',
    name: "Lowe's Installation",
    tagline: "Lowe's installer network — use their locator on-site",
    // Their old /l/install/install-services slug 404s now. Their
    // services hub keeps moving so we point at the home page; the
    // user clicks "Services" in their nav once they land.
    url: 'https://www.lowes.com/',
    tier: 'chain',
    isLandingOnly: true,
  },
  {
    key: 'mr_handyman',
    name: 'Mr. Handyman',
    tagline: 'General home repairs',
    url: 'https://www.mrhandyman.com/',
    tier: 'chain',
    isLandingOnly: true,
  },
  {
    key: 'one_hour_heat',
    name: 'One Hour Heating & Air',
    tagline: 'Punctual HVAC service franchise',
    url: 'https://www.onehourheatandair.com/',
    tier: 'chain',
    isLandingOnly: true,
  },
  {
    key: 'mr_electric',
    name: 'Mr. Electric',
    tagline: 'Licensed electrical service franchise',
    url: 'https://www.mrelectric.com/',
    tier: 'chain',
    isLandingOnly: true,
  },
  {
    key: 'aire_serv',
    name: 'Aire Serv',
    tagline: 'HVAC service franchise',
    url: 'https://www.aireserv.com/',
    tier: 'chain',
    isLandingOnly: true,
  },
  {
    key: 'lawn_doctor',
    name: 'Lawn Doctor',
    tagline: 'Lawn care + treatments',
    url: 'https://www.lawndoctor.com/',
    tier: 'chain',
    isLandingOnly: true,
  },
];

export function listFeaturedProviders(): ProProvider[] {
  return PROVIDERS_FEATURED;
}

export function listSearchProviders(): ProProvider[] {
  return PROVIDERS_SEARCH;
}

export function listChainProviders(): ProProvider[] {
  return PROVIDERS_CHAIN;
}

/**
 * Emergency-flagged providers, regardless of tier. Surfaced when the
 * user picks the "Emergency" category — the assumption is that at 2am
 * with water flooding the basement, the user wants a name and a phone
 * number, not five directories to browse.
 */
export function listEmergencyProviders(): ProProvider[] {
  return PROVIDERS_CHAIN.filter((p) => p.isEmergency);
}

export function buildProviderUrl(
  provider: ProProvider,
  trade: string,
  zip: string | null
): string {
  return provider.url
    .replace('{trade}', encodeURIComponent(trade))
    .replace('{zip}', encodeURIComponent(zip ?? ''));
}

export function providerSupportsTrade(provider: ProProvider): boolean {
  return provider.url.includes('{trade}');
}
